import React, { useEffect, useState } from "react";
import { Box, Grid, Typography, IconButton, Menu, MenuItem, TextField } from "@mui/material";
import TableComponent from "components/grid";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useParams } from "react-router-dom";
import moment from "moment";
import { LuEye, LuSearch } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { DEFAULT_DATE_FORMAT } from "config";
import CustomPagination from "components/CustomPagination";
import { useLazyGetAllWorkflowConfigrationQuery } from "store/slices/apis/workflowConfigrationSlice";
import TriggerWorkflow from "./TriggerWorkflow";
import TrackerPageHeader from "components/TrackerPageHeader";

const InitiateWorkflow = () => {
    const navigate = useNavigate();
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const { projectId } = useParams();

    // sorting asc desc
    const [sortOrder, setSortOrder] = React.useState('desc');
    const [sortBy, setSortBy] = React.useState('createdDate');

    // page size
    const [pageNumber, setPageNumber] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);

    const [loading, setLoading] = useState(false);

    const [workflowTriggerModal, setWorkflowTriggerModal] = useState(false);
    const [searchText, setSearchText] = useState("");

    const [filters, setFilters] = useState({
        ProjectId: projectId,
        SortBy: sortBy,
        SortOrder: sortOrder,
        PageNumber: pageNumber,
        PageSize: pageSize,
        SearchText: "",
    });

    const [trigger, { data: workflowList, isFetching }] = useLazyGetAllWorkflowConfigrationQuery();

    const handlePageChange = (newPage) => {
        setPageNumber(newPage);
        const updatedFilters = {
            ...filters,
            PageNumber: newPage,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPageNumber(1);
        const updatedFilters = {
            ...filters,
            PageSize: newSize,
            PageNumber: 1,
        };
        setFilters(updatedFilters);
        trigger(updatedFilters);
    };


    useEffect(() => {
        trigger(filters);
    }, []);

    const handleRequestSort = (property) => {
        const isAsc = sortBy === property && sortOrder === 'asc';
        const updatedFilters = {
            ...filters,
            SortBy: property,
            SortOrder: isAsc ? 'desc' : 'asc'

        };
        setFilters(updatedFilters);
        setSortOrder(isAsc ? 'desc' : 'asc');
        setSortBy(property);
        trigger(updatedFilters);

    };

    //list action menu
    const handleMenuOpen = (event, row) => {
        event.stopPropagation(); // prevent row click if any
        setMenuAnchorEl(event.currentTarget);
        setMenuRow(row);
    };
    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuRow(null);
    };
    const handleMenuView = () => {
        if (menuRow) {
            handleViewClick(menuRow);
        }
        handleMenuClose();
    };

    const handleWorkflowTrigger = () => {
        if (workflowTriggerModal) {
            handleMenuClose();
        }
        setWorkflowTriggerModal((prev) => !prev);
    }

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (searchText !== filters.SearchText) {
                const updatedFilters = {
                    ...filters,
                    SearchText: searchText,
                    PageNumber: 1,
                };
                setFilters(updatedFilters);
                trigger(updatedFilters);
                // setCurrentPage(1);
            }
        }, 500);
        return () => clearTimeout(delayDebounceFn);
    }, [searchText]);

    const columns = [
        {
            name: "",
            field: "actions",
            width: "10px",
            sticky: true,
            renderer: (row) => (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, row)}
                    >
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                </Box>
            ),
        },
        {
            name: "Workflow Name",
            field: "workflowName",
            width: "360px",
            renderer: (row) => (
                <Typography sx={{
                    maxWidth: "320px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontFamily: "Outfit, sans-serif",
                    fontSize: "13px",
                    lineHeight: "18px",
                    fontWeight: 600,
                    color: "#172033",
                }}>
                    {row?.workflowName || "-"}
                </Typography>
            ),
        },
        {
            name: 'Status',
            field: 'status',
            renderer: (row) => (
                <Typography sx={{ fontFamily: "Outfit, sans-serif", fontSize: "12px", color: "#475569" }}>
                    {row?.isActive ? "Active" : "Inactive"}
                </Typography>
            )
        },
        {
            name: "Entity Type",
            field: "entityName",
            // width: "7%",
        },
        {
            name: "Total Step",
            field: "totalSteps",
            // width: "7%",
        },
        {
            name: "Created at",
            field: "createdDate",
            // width: "10%",
            renderer: (row) => (
                moment(row?.createdDate).format(DEFAULT_DATE_FORMAT)
            ),
        },
        {
            name: "Last Updated at",
            field: "updateDate",
            // width: "10%",
            renderer: (row) => (
                <>
                    {row?.updateDate ?
                        moment(row?.updateDate).format(DEFAULT_DATE_FORMAT)
                        : 'N/A'
                    }
                </>
                // moment(row?.updateDate).format(DEFAULT_DATE_FORMAT)
            ),
        },
    ];

    const handleViewClick = (row) => {
        // console.log(row);
        navigate(`/projects/${projectId}/initiate-workflow/${row?.workflowConfigurationId}/${row?.entityName}/workflow-history`);
        // setIsViewTracker((prev) => {
        //     const newState = !prev;
        //     setRowData(newState ? row : null);
        //     return newState;
        // });
    }

    return (
        <>
            <TrackerPageHeader title="Workflows" description="Initiate configured workflows and review their current progress." />
            <Grid container spacing={1.5} sx={{ p: 2 }}>
                <Grid size={12}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", py: 0.5 }}>
                        <TextField
                            placeholder="Search workflows"
                            value={searchText}
                            onChange={handleSearchChange}
                            variant="outlined"
                            size="small"
                            InputProps={{
                                startAdornment: <LuSearch size={16} style={{ marginRight: '8px', color: '#64748B' }} />,
                            }}
                            sx={{
                                width: { xs: "100%", sm: "360px", md: "420px" },
                                backgroundColor: "#FFFFFF",
                                "& .MuiOutlinedInput-root": {
                                    minHeight: "36px",
                                    borderRadius: "3px",
                                    fontFamily: "Outfit, sans-serif",
                                    fontSize: "13px",
                                    "& fieldset": { borderColor: "#CBD5E1" },
                                    "&:hover fieldset": { borderColor: "#94A3B8" },
                                    "&.Mui-focused fieldset": { borderColor: "#2563EB", borderWidth: "1px" },
                                },
                                "& .MuiInputBase-input::placeholder": { color: "#94A3B8", opacity: 1 },
                            }}
                            type="search"
                        />
                    </Box>
                </Grid>
                <Grid size={12}>
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                        PaperProps={{
                            elevation: 0,
                            sx: {
                                mt: 1,
                                borderRadius: "3px",
                                minWidth: 140,
                                border: "1px solid #E5E9EB",
                                boxShadow: "0px 10px 30px rgba(15, 23, 42, 0.12)",
                                overflow: "hidden",
                            },
                        }}
                        MenuListProps={{
                            sx: {
                                py: 0.5,
                            },
                        }}
                    >
                        <MenuItem
                            onClick={handleMenuView}
                            sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontSize: "14px",
                                fontWeight: 400,
                                px: 1.5,
                                py: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                color: "#111827",
                                "&:hover": {
                                    backgroundColor: "#F6F8F9",
                                },
                            }}
                        >
                            <LuEye size={16} style={{ color: "#4B5563" }} />
                            <span>View</span>
                        </MenuItem>
                        <MenuItem
                            onClick={handleWorkflowTrigger}
                            sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontSize: "14px",
                                fontWeight: 400,
                                px: 1.5,
                                py: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                color: "#111827",
                                "&:hover": {
                                    backgroundColor: "#F6F8F9",
                                },
                            }}
                        >
                            <LuEye size={16} style={{ color: "#4B5563" }} />
                            <span>Start Workflow</span>
                        </MenuItem>

                        {/* <MenuItem
                            onClick={handleMenuEdit}
                            sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontSize: "14px",
                                fontWeight: 400,
                                px: 1.5,
                                py: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                color: "#111827",
                                "&:hover": {
                                    backgroundColor: "#F6F8F9",
                                },
                            }}
                        >
                            <EditIcon style={{ width: 14, height: 14 }} />
                            <span>Edit</span>
                        </MenuItem> */}
                    </Menu>
                    <TableComponent
                        columns={columns}
                        data={workflowList?.data}
                        createSortHandler={handleRequestSort}
                        isLoading={loading}
                        order={sortOrder}
                        orderBy={sortBy}
                    />
                    <CustomPagination
                        totalCount={workflowList?.totalRecords || 0}
                        pageSize={pageSize}
                        page={pageNumber}
                        onPageChange={handlePageChange}
                        setCurrentPage={setPageNumber}
                        onPageSizeChange={handlePageSizeChange}
                    />
                </Grid>
            </Grid>
            {workflowTriggerModal && (
                <TriggerWorkflow
                    open={workflowTriggerModal}
                    handleClose={handleWorkflowTrigger}
                    title={menuRow?.workflowName}
                    rowData={menuRow}
                />
            )}
        </>
    );
}

export default InitiateWorkflow;
